import { forwardRef, useLayoutEffect, useRef, useState } from "react";
import { core } from "@servicetitan/hammer-token";

import { DataTrackingId } from "../../types";
import { SrOnly } from "../SrOnly";
import { useTrackingId, useMergeRefs } from "../../hooks";

import {
  Pagination as BasePagination,
  type PaginationProps as BasePaginationProps,
} from "./internal/Pagination";
import { usePaginationArray } from "./internal/usePaginationArray";

import cx from "classnames";
import styles from "./Pagination.module.scss";

/**
 * Props for the Pagination component
 * @property {number} page - The current page number
 * @property {number} itemsPerPage - Number of items displayed per page
 * @property {number[]} [itemsPerPageOptions] - Available options for items per page selection
 * @property {number} [totalItemCount] - Total number of items across all pages
 * @property {boolean} [showCount] - Whether to display the item count information
 * @property {(page: number) => void} [onPageChange] - Callback when page changes
 * @property {(itemsPerPage: number) => void} [onItemsPerPageChange] - Callback when items per page changes
 * @property {string} [itemsPerPageLabel] - Custom label displayed before the items-per-page selector. Defaults to "Rows per page"
 * @extends Omit<BasePaginationProps, "children">
 * @extends DataTrackingId
 */
export type PaginationProps = Omit<BasePaginationProps, "children"> &
  DataTrackingId & {
    page: number;
    itemsPerPage: number;
    itemsPerPageOptions?: number[];
    totalItemCount?: number;
    showCount?: boolean;
    onPageChange?: (page: number) => void;
    onItemsPerPageChange?: (itemsPerPage: number) => void;
    itemsPerPageLabel?: string;
  };

/**
 * Pagination component for navigating through paginated content with automatic page array generation.
 *
 * Features:
 * - Automatic page array generation with overflow handling
 * - Supports items per page selection with customizable options
 * - Displays item count information with screen reader support
 * - Handles edge cases like invalid page numbers
 * - Includes previous/next navigation buttons
 * - Supports overflow menus for large page ranges
 * - Fully accessible with proper ARIA attributes
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <Pagination
 *   page={1}
 *   itemsPerPage={10}
 *   totalItemCount={100}
 *   itemsPerPageOptions={[10, 20, 50]}
 *   showCount={true}
 *   onPageChange={(page) => console.log('Page changed to:', page)}
 *   onItemsPerPageChange={(itemsPerPage) => console.log('Items per page:', itemsPerPage)}
 * />
 */
export const Pagination = forwardRef<HTMLDivElement, PaginationProps>(
  (props, ref) => {
    const {
      "aria-label": ariaLabel,
      page,
      itemsPerPage,
      itemsPerPageOptions,
      totalItemCount: totalItemCountProp,
      showCount,
      onPageChange,
      onItemsPerPageChange,
      itemsPerPageLabel,
      className,
      ...rest
    } = props;

    const data = {
      "aria-label": ariaLabel,
    };

    const trackingId = useTrackingId({
      name: "Pagination",
      data,
      hasOverride: !!rest["data-tracking-id"],
    });
    const totalItemCount = totalItemCountProp ?? 0;
    const totalPages = Math.ceil(totalItemCount / itemsPerPage);
    const elRef = useRef<HTMLDivElement>(null);
    const combinedRef = useMergeRefs([elRef, ref]);
    const [isNarrow, setIsNarrow] = useState(false);

    const pageArray = usePaginationArray({
      page,
      totalPages,
      maxArrayLength: 7,
    });

    // Check if current page is available in the pageArray, fallback to page 1 if not
    const isPageAvailable = pageArray.some(
      (item) => item.type === "page" && item.page === page,
    );

    if (!isPageAvailable && page !== 1 && totalPages > 0) {
      // Use requestAnimationFrame to avoid calling onPageChange during render
      requestAnimationFrame(() => {
        onPageChange?.(1);
      });
    }

    const firstItem = totalItemCount > 0 ? 1 + (page - 1) * itemsPerPage : 0;
    const lastItem = Math.min(firstItem - 1 + itemsPerPage, totalItemCount);
    const itemRangeText =
      totalItemCountProp === 0
        ? "0 items"
        : `${firstItem} - ${lastItem} of ${totalItemCount} items`;

    useLayoutEffect(() => {
      const resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) {
          setIsNarrow(
            entry.contentRect.width <
              Number(core.primitive.BreakpointSm.value.replace("px", "")),
          );
        }
      });
      if (elRef.current) {
        resizeObserver.observe(elRef.current);
      }
      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    return (
      <BasePagination
        ref={combinedRef}
        data-tracking-id={trackingId}
        className={cx(styles.pagination, className)}
        aria-label={ariaLabel || "Pagination navigation"}
        {...rest}
      >
        <BasePagination.List
          page={page}
          totalPages={totalPages}
          hasTotalItemCount={totalItemCountProp !== undefined}
          onPageChange={(page) => onPageChange?.(page)}
        >
          {pageArray.map((item) => {
            if (item.type === "page") {
              return (
                <BasePagination.Item
                  key={item.page}
                  page={item.page}
                  isActive={item.page === page}
                  onClick={() => onPageChange?.(item.page)}
                />
              );
            }
            return (
              <BasePagination.OverflowMenu
                key={item.startPage}
                startPage={item.startPage}
                endPage={item.endPage}
                length={item.length}
                onItemClick={(item) => onPageChange?.(item)}
              />
            );
          })}
        </BasePagination.List>
        {isNarrow === false && (
          <>
            {itemsPerPageOptions && (
              <BasePagination.ItemsPerPageMenu
                itemsPerPage={itemsPerPage}
                itemsPerPageOptions={itemsPerPageOptions}
                onItemsPerPageChange={(itemsPerPage) =>
                  onItemsPerPageChange?.(itemsPerPage)
                }
                itemsPerPageLabel={itemsPerPageLabel}
              />
            )}
            {showCount && totalItemCountProp !== undefined && (
              <BasePagination.TotalCount itemRangeText={itemRangeText} />
            )}
          </>
        )}
        <SrOnly aria-live="polite" aria-atomic="true">
          Page {page} of {totalPages}, {itemRangeText}
        </SrOnly>
      </BasePagination>
    );
  },
);

Pagination.displayName = "Pagination";
